import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { RouteComponentProps, withRouter } from 'react-router';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { getClubProgramme } from 'Src/helpers/service/admin/clubProgrammes';
import { ClubProgrammeBookingsBoard } from './ClubProgrammeBookingsBoard';
import { ClubProgrammeBookingsPageHeader } from './components/ClubProgrammeBookingsPageHeader';
import { ClubProgrammeErrorModal } from './components/ClubProgrammeErrorModal';
import './ClubProgrammes.css';

interface MatchParams {
  programmeId: string;
}

interface Props extends RouteComponentProps<MatchParams> {
  user: AppUser;
}

interface State {
  isLoading: boolean;
  programme: any;
  errorMessage: string;
  isErrorModalOpen: boolean;
  activationRequestToken: number;
  canOpenActivationPreview: boolean;
  selectionMode: string;
}

class ClubProgrammeBookingsPage extends React.Component<Props, State> {
  state: State = {
    isLoading: true,
    programme: null,
    errorMessage: '',
    isErrorModalOpen: false,
    activationRequestToken: 0,
    canOpenActivationPreview: false,
    selectionMode: ''
  };

  componentDidMount() {
    this.load();
  }

  load = () => {
    const { user } = this.props;
    const programmeId = String(propz.get(this.props, ['match', 'params', 'programmeId'], ''));

    if (!programmeId) {
      this.setState({
        isLoading: false,
        isErrorModalOpen: true,
        errorMessage: 'Programme id is missing.'
      });
      return;
    }

    this.setState({ isLoading: true });

    BPromise.resolve(getClubProgramme(user, programmeId))
      .then(programme => {
        this.setState({
          programme,
          isLoading: false,
          selectionMode: String(propz.get(programme, ['selectionRules', 'mode'], ''))
        });
      })
      .catch(err => {
        console.error('ClubProgrammeBookingsPage load error:', err);
        const errorText = propz.get(
          err,
          ['response', 'data', 'details', 'text'],
          propz.get(err, ['response', 'data', 'error'], 'Failed to load programme.')
        );

        this.setState({
          isLoading: false,
          isErrorModalOpen: true,
          errorMessage: errorText
        });
      });
  };

  goBack = () => {
    const from = propz.get(this.props, ['location', 'state', 'from']);

    if (from && from.pathname) {
      this.props.history.push({
        pathname: String(from.pathname),
        search: String(from.search || '')
      });
      return;
    }

    this.props.history.push('/clubProgrammes');
  };

  onActivateProgrammesClick = () => {
    if (!this.state.canOpenActivationPreview) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'Please confirm all pending programme allocations before activating clubs.'
      });
      return;
    }

    this.setState(prev => ({
      activationRequestToken: prev.activationRequestToken + 1
    }));
  };

  render() {
    const { programme, isLoading } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    if (!programme) {
      return (
        <div className="pbProgrammeBookingsPage">
          <ClubProgrammeErrorModal
            isOpen={this.state.isErrorModalOpen}
            errorMessage={this.state.errorMessage}
            onClose={() => this.setState({ isErrorModalOpen: false, errorMessage: '' })}
          />
          <header className="pbPageHeader pbPageHeader--error">
            <div className="pbPageHeaderMain">
              <button type="button" className="pbBackLink" onClick={this.goBack}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m14.5 5-7 7 7 7" />
                </svg>
                <span>Back to activity programmes</span>
              </button>
              <div className="pbPageTitleRow">
                <h1>Booking manager</h1>
              </div>
            </div>
          </header>
          <main className="pbProgrammeBookingsError">
            <strong>Programme not found</strong>
            <p>The programme could not be loaded. Return to Activity programmes and try again.</p>
          </main>
        </div>
      );
    }

    return (
      <div className="pbProgrammeBookingsPage">
        <ClubProgrammeErrorModal
          isOpen={this.state.isErrorModalOpen}
          errorMessage={this.state.errorMessage}
          onClose={() => this.setState({ isErrorModalOpen: false, errorMessage: '' })}
        />

        <ClubProgrammeBookingsPageHeader
          programmeName={String(propz.get(programme, ['name'], 'Programme'))}
          programmeStatus={String(propz.get(programme, ['status'], 'DRAFT'))}
          selectionMode={this.state.selectionMode}
          isCompleted={!!propz.get(programme, ['completion', 'isCompleted'], false)}
          canOpenActivationPreview={this.state.canOpenActivationPreview}
          onActivateClick={this.onActivateProgrammesClick}
          onBackClick={this.goBack}
        />

        <ClubProgrammeBookingsBoard
          user={this.props.user}
          programme={programme}
          activationRequestToken={this.state.activationRequestToken}
          onActivationAvailabilityChange={(canOpen: boolean) => {
            if (canOpen !== this.state.canOpenActivationPreview) {
              this.setState({ canOpenActivationPreview: canOpen });
            }
          }}
          onSelectionModeChange={(selectionMode: string) => {
            if (selectionMode !== this.state.selectionMode) {
              this.setState({ selectionMode });
            }
          }}
        />
      </div>
    );
  }
}

export default withRouter(ClubProgrammeBookingsPage);
